<template>
  <div class="overlay">
    <div v-if="isLoading" class="preload-background">
    <BuildingLoader />
  </div>
  <canvas ref="canvasRef" class="preload-canvas"></canvas>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'
import BuildingLoader from './BuildingLoader.vue'
const props = defineProps({
  isLoading: {
    type: Boolean,
    required: true
  }
})

const canvasRef = ref(null)
let gl = null
let animationId = null
let program = null
let timeUniformLocation = null
let resolutionUniformLocation = null
let animationDelayUniformLocation = null
let animationDurationUniformLocation = null

// Animation timing
const ANIMATION_DELAY = 0.0 // Start immediately after isLoading becomes false
const ANIMATION_DURATION = 2.0

let startTime = null

// WebGL shader utils
function createShader(gl, type, source) {
  const shader = gl.createShader(type)
  gl.shaderSource(shader, source)
  gl.compileShader(shader)

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }
  return shader
}

function createProgram(gl, vertexShader, fragmentShader) {
  if (!vertexShader || !fragmentShader) return null

  const program = gl.createProgram()
  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.linkProgram(program)

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program))
    gl.deleteProgram(program)
    return null
  }
  return program
}

const vertexShaderSource = `attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0, 1);
}`

const fragmentShaderSource = `precision mediump float;
uniform vec2 u_resolution;
uniform float u_time;
uniform float u_animation_delay;
uniform float u_animation_duration;

float random(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}

float noise(vec2 st) {
  vec2 i = floor(st);
  vec2 f = fract(st);

  float a = random(i);
  float b = random(i + vec2(1.0, 0.0));
  float c = random(i + vec2(0.0, 1.0));
  float d = random(i + vec2(1.0, 1.0));

  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

float cloudShape(vec2 pixelCoords, vec2 position, float size, float time, float animProgress) {
  float growFactor = 1.0 + animProgress * 60.0;
  float baseSize = size * growFactor;

  vec2 p = pixelCoords - position;
  float xStretch = 1.5;
  float yStretch = 0.7;

  float topWave = sin(p.x * 0.01 + time * 1.0) * 20.0;
  p.y += topWave * smoothstep(0.0, 1.0, p.y / (baseSize * yStretch));

  float bottomWave = sin(p.x * 0.015 + time * 0.75) * 25.0;
  float rightBias = smoothstep(-baseSize * xStretch, baseSize * xStretch, p.x) * 1.5;
  p.y += bottomWave * rightBias * smoothstep(0.0, -1.0, p.y / (baseSize * yStretch));

  float dist = length(vec2(p.x / (baseSize * xStretch), p.y / (baseSize * yStretch)));
  float edge = smoothstep(1.0, 0.8, dist);
  return 1.0 - edge;
}

void main() {
  vec2 st = gl_FragCoord.xy / u_resolution;
  vec2 pixelCoords = st * u_resolution;
  vec2 center = u_resolution * 0.5;
  float time = u_time;

  float delayedTime = max(0.0, time - u_animation_delay);
  float animProgress = min(delayedTime / u_animation_duration, 1.0);
  bool animationStarted = delayedTime > 0.0;

  float cloud1 = 0.0;
  float cloud2 = 0.0;
  float cloud3 = 0.0;
  float cloud4 = 0.0;
  float cloud5 = 0.0;

  if (animationStarted) {
    cloud1 = cloudShape(pixelCoords, center + vec2(-400.0, 250.0), 8.0, time * 0.5, animProgress);
    cloud2 = cloudShape(pixelCoords, center + vec2(400.0, 300.0), 7.0, time * 0.5 + 1.0, animProgress);
    cloud3 = cloudShape(pixelCoords, center + vec2(350.0, -50.0), 6.5, time * 0.5 + 2.0, animProgress);
    cloud4 = cloudShape(pixelCoords, center + vec2(-200.0, -250.0), 7.5, time * 0.5 + 0.5, animProgress);
    cloud5 = cloudShape(pixelCoords, center + vec2(100.0, 150.0), 6.0, time * 0.5 + 1.5, animProgress);
  }

  float finalShape = min(min(min(min(cloud1, cloud2), cloud3), cloud4), cloud5);

  if (animProgress >= 0.99999) {
    finalShape = 0.0;
  }
  // cancas color
  vec3 color = vec3(27.0/255.0, 27.0/255.0, 30.0/255.0); // rgb(26, 27, 65)
  float alpha = animationStarted ? finalShape : 1.0;
  gl_FragColor = vec4(color, alpha);
}`

// WebGL setup
function setupWebGL() {
  const canvas = canvasRef.value
  if (!canvas) return

  gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true })
  if (!gl) {
    console.error('WebGL not supported')
    return
  }

  gl.enable(gl.BLEND)
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource)
  program = createProgram(gl, vertexShader, fragmentShader)

  if (!program) return
  gl.useProgram(program)

  const positionBuffer = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
  const positions = [-1, -1, -1, 1, 1, 1, -1, -1, 1, 1, 1, -1]
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW)

  const posAttrLoc = gl.getAttribLocation(program, 'a_position')
  gl.enableVertexAttribArray(posAttrLoc)
  gl.vertexAttribPointer(posAttrLoc, 2, gl.FLOAT, false, 0, 0)

  resolutionUniformLocation = gl.getUniformLocation(program, 'u_resolution')
  timeUniformLocation = gl.getUniformLocation(program, 'u_time')
  animationDelayUniformLocation = gl.getUniformLocation(program, 'u_animation_delay')
  animationDurationUniformLocation = gl.getUniformLocation(program, 'u_animation_duration')
}

const emit = defineEmits(['onComplete'])
let hasEmittedComplete = false
function render(currentTime) {
  if (!gl || !program) return
  resizeCanvas()

  const canvas = canvasRef.value
  const elapsed = (currentTime - startTime) / 1000

  gl.viewport(0, 0, canvas.width, canvas.height)
  gl.uniform2f(resolutionUniformLocation, canvas.width, canvas.height)
  gl.uniform1f(timeUniformLocation, elapsed)
  gl.uniform1f(animationDelayUniformLocation, ANIMATION_DELAY)
  gl.uniform1f(animationDurationUniformLocation, ANIMATION_DURATION)

  gl.drawArrays(gl.TRIANGLES, 0, 6)

  // Check if animation is complete and emit if needed
  const animProgress = Math.min((elapsed - ANIMATION_DELAY) / ANIMATION_DURATION, 1.0)
  if (animProgress >= 0.99999 && !hasEmittedComplete) {
    hasEmittedComplete = true
    emit('onComplete')
  }

  animationId = requestAnimationFrame(render)
}

function resizeCanvas() {
  const canvas = canvasRef.value
  if (!canvas) return

  const displayWidth = window.innerWidth
  const displayHeight = window.innerHeight

  if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
    canvas.width = displayWidth
    canvas.height = displayHeight
  }
}

watch(() => props.isLoading, (newVal) => {
  if (!newVal) {
    startTime = performance.now()
    animationId = requestAnimationFrame(render)
  }
})

onMounted(() => {
  setupWebGL()
  window.addEventListener('resize', resizeCanvas)
  resizeCanvas()
})

onBeforeUnmount(() => {
  if (animationId) cancelAnimationFrame(animationId)
  window.removeEventListener('resize', resizeCanvas)
})
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 998;
}
.preload-background {
  position: absolute;
  inset: 0;
  background-color: #1b1b1e;
  /* یا مثلا عکس background */
  z-index: 998;
  /* یک پله پایین‌تر از canvas */
  display: flex;
  align-items: center;
  justify-content: center;
}

.branding h1 {
  color: white;
  font-size: 2rem;
  font-family: sans-serif;
}

.preload-canvas {
  position: absolute;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 999;
  pointer-events: none;
}
</style>
